import React, { ReactNode } from 'react';
import { Box, styled, Tabs } from '@mui/material';
import { Route, Redirect, RouteProps, Switch, useLocation } from 'react-router-dom';
import { Tab, Route as RouteType } from 'routing/types';
import TabContainerTab from './TabContainerTab';

export interface TabContainerTabArgs extends RouteProps {
	path: Tab;
	label: string;
	badge?: string | number;
	hidden?: boolean;
	disabled?: boolean;
	locked?: boolean;
}

export interface TabContainerProps {
	basePath: RouteType;
	defaultTab?: Tab;
	tabs: TabContainerTabArgs[];
	renderTabs?: (tabs: ReactNode) => ReactNode;
	tabStyle?: 'primary' | 'secondary';
}
const TabContainer = (props: TabContainerProps) => {
	const location = useLocation();
	const { basePath, defaultTab, tabs, renderTabs, tabStyle = 'primary' } = props;
	const defaultPath = `${basePath}/${defaultTab ?? tabs[0].path}`;

	const filteredTabs = tabs.filter((tab) => !tab.hidden);
	const currentPath = location.pathname.replace(`${basePath}/`, '');
	const activeTabIndex = filteredTabs.findIndex((tab) => currentPath.startsWith(tab.path));

	const TabsComponent = tabStyle === 'primary' ? PrimaryTabs : SecondaryTabs;

	const _tabs = (
		<TabsComponent
			value={activeTabIndex > -1 ? activeTabIndex : 0}
			sx={{ mb: 2 }}
			variant="scrollable"
			scrollButtons={false}
		>
			{filteredTabs.map((tab, index) => (
				<TabContainerTab
					key={tab.path}
					tab={tab}
					index={index}
					active={index === activeTabIndex}
					basePath={basePath}
				/>
			))}
		</TabsComponent>
	);

	return (
		<Box>
			{renderTabs ? renderTabs(_tabs) : _tabs}
			<Box>
				<Switch>
					{filteredTabs.map((tab) => {
						const { label, path, hidden, ...routeProps } = tab;
						const fullPath = `${basePath}/${path}`;
						if (hidden) return null;
						return <Route key={path} path={fullPath} {...routeProps} />;
					})}
					<Redirect to={defaultPath} />
				</Switch>
			</Box>
		</Box>
	);
};

const PrimaryTabs = styled(Tabs)(({ theme }) => ({
	'& .MuiTabs-indicator': {
		height: '100%',
		zIndex: 0,
		background: theme.palette.primary.main,
		opacity: 0.1,
		borderRadius: 4,
	},
	'& .MuiButtonBase-root': {
		borderRadius: 4,
	},
}));

const SecondaryTabs = styled(Tabs)(({ theme }) => ({
	height: '32px',
	minHeight: '32px',
	'& .MuiTabs-indicator': {
		height: '100%',
		zIndex: 0,
		background: theme.palette.primary.main,
		opacity: 0.1,
		borderRadius: 4,
	},
	'& .MuiTab-root': {
		padding: theme.spacing(1, 2),
		minHeight: '32px',
		fontSize: '1.4rem',
		lineHeight: '16px',
	},
	'& .MuiButtonBase-root': {
		borderRadius: 4,
	},
}));

export default TabContainer;
